import { useState, useRef, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import styles from "./TopUpView.module.scss";
import { Dropdown } from "@/shared/ui/Dropdown/Dropdown";
import { CardCarousel } from "@/modules/Balance/ui/CardCarousel";
import { AddCardForm } from "@/modules/Balance/ui/AddCardForm";
import { AutoTopUpForm } from "@/modules/Balance/ui/AutoTopUpForm";
import { CardIcon } from "@/modules/Balance/assets/CardIcon";
import { SBPIcon } from "@/modules/Balance/assets/SBPIcon";
import { Switch } from "@/shared/ui/Switch/Switch";
import { RefreshIcon } from "@/modules/Balance/assets/RefreshIcon";
import { useQuery } from "@tanstack/react-query";
import { ApiBalance } from "@/shared/api/balance/api-balance";
import { useSuppliersStore } from "@/shared/stores/suppliersStore";
import { useTopUpStore } from "@/modules/Balance/model/top-up-store";
import { useDisableAutoTopUp } from "@/modules/Balance/hooks/use-disable-auto-top-up";
import {
  getSavedMethods,
  type ServerPaymentMethod,
} from "@/shared/api/payment/api-payment";
import { useEnrollPaymentMethod } from "../hooks/use-enroll-payment-method";
import { useTopUpBalance } from "@/shared/hooks/useTopUpBalance";
import { useToastStore } from "@/shared/stores/toastStore";
import { topUpSchema, type TopUpFormValues } from "../model/top-up-shema";
import { cn } from "@/shared/lib/utils";
import { BonusProgress } from "./BonusProgress";

type View = "default" | "addCard";

interface TopUpViewProps {
  onClose: () => void;
  onTopUpSuccess: (topUpId: string, totalAmount: number) => void;
}

const IS_BONUS_SYSTEM_ENABLED = true;

export const TopUpView = ({ onClose, onTopUpSuccess }: TopUpViewProps) => {
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [view, setView] = useState<View>("default");
  const [autoTopUp, setAutoTopUp] = useState(false);
  const autoTopUpFormRef = useRef<HTMLDivElement>(null);

  const { selectedSupplierId } = useSuppliersStore();
  const { autoTopUpId, setAutoTopUpId, selectedCardId, setSelectedCardId } =
    useTopUpStore();
  const { showToast } = useToastStore();
  const { mutate: disableAutoTopUp } = useDisableAutoTopUp();
  const { mutate: enrollPaymentMethod, isPending: isAddingCard } =
    useEnrollPaymentMethod();
  const { mutate: topUp, isPending } = useTopUpBalance();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = useForm<TopUpFormValues>({
    resolver: zodResolver(topUpSchema),
    mode: "onSubmit",
    defaultValues: {
      amount: "",
      email: "",
    },
  });

  const amountValue = watch("amount");
  const emailValue = watch("email");

  const apiBalance = new ApiBalance();

  const { data: bonusTiersResponse } = useQuery({
    queryKey: ["bonus-tiers"],
    queryFn: () => apiBalance.getBonusTiers(),
    staleTime: Infinity,
  });

  const bonusTiers = bonusTiersResponse?.data.data ?? [];

  const {
    data: autoTopUpInfo,
    refetch: refetchAutoTopUpInfo,
    isLoading: isAutoTopUpLoading,
  } = useQuery({
    queryKey: ["auto-top-up", selectedSupplierId],
    queryFn: async () => {
      if (!selectedSupplierId) throw new Error("supplier id is required");
      const response = await apiBalance.getAutoTopUpInfo(selectedSupplierId);
      return response.data;
    },
    enabled: !!selectedSupplierId,
  });

  const { data: savedMethodsResponse, isLoading: isLoadingSavedMethods } =
    useQuery({
      queryKey: ["saved-payment-methods"],
      queryFn: () => getSavedMethods(),
    });

  const savedCards = useMemo(
    () =>
      savedMethodsResponse?.data.map((card: ServerPaymentMethod) => ({
        payment_id: card.payment_id,
        type: card.card?.card_type.toLowerCase() || "",
        last4:
          card.card?.last4 ||
          card.title.split(" ").pop()?.replace(/\D/g, "") ||
          "",
        method: card.method,
      })) || [],
    [savedMethodsResponse],
  );

  useEffect(() => {
    if (autoTopUpInfo) {
      setAutoTopUp(autoTopUpInfo.enabled);
      setAutoTopUpId(autoTopUpInfo.id);
      if (autoTopUpInfo.payment_method?.id) {
        setSelectedCardId(autoTopUpInfo.payment_method.id);
      }
    } else if (savedCards.length > 0) {
      setSelectedCardId(savedCards[0].payment_id);
    }
  }, [autoTopUpInfo, savedCards, setAutoTopUpId, setSelectedCardId]);

  const handleAutoTopUpChange = (checked: boolean) => {
    setAutoTopUp(checked);
    if (!checked && autoTopUpId) {
      disableAutoTopUp(autoTopUpId, {
        onSuccess: () => {
          refetchAutoTopUpInfo();
        },
      });
    }
  };

  const numericAmount = useMemo(
    () => parseFloat(amountValue) || 0,
    [amountValue],
  );

  const { bonusAmount, totalAmount } = useMemo(() => {
    if (!IS_BONUS_SYSTEM_ENABLED || !bonusTiersResponse) {
      return { bonusAmount: 0, totalAmount: numericAmount };
    }

    const amountInPennies = numericAmount * 100;
    const applicableTier = bonusTiersResponse.data.data.find(
      (tier) =>
        amountInPennies >= tier.min_amount &&
        (tier.max_amount === null || amountInPennies <= tier.max_amount),
    );

    const percent = applicableTier ? applicableTier.bonus_percent : 0;
    const bonus = Math.floor(numericAmount * (percent / 100));
    return { bonusAmount: bonus, totalAmount: numericAmount + bonus };
  }, [numericAmount, bonusTiersResponse]);

  const onSubmit = (formData: TopUpFormValues) => {
    if (!selectedSupplierId) return;

    const { amount, email } = formData;
    const savePaymentMethod =
      paymentMethod === "sbp" || paymentMethod === "card";

    const requestData = {
      supplier_id: selectedSupplierId,
      amount: Number(amount) * 100,
      receipt_email: email,
      save_payment_method: savePaymentMethod,
      payment_confirmation: null,
      saved_payment_method_id: selectedCardId,
    };

    topUp(requestData, {
      onSuccess: (result) => {
        onTopUpSuccess(result.top_up_id, totalAmount);
      },
      onError: () => {
        showToast("Ошибка пополнения баланса", "error");
      },
    });
  };

  const quickAmounts = [1000, 3000, 5000, 10000];
  const paymentOptions = [
    { value: "card", label: "Банковская карта", icon: <CardIcon /> },
    { value: "sbp", label: "СБП", icon: <SBPIcon /> },
  ];

  const handleCloseModal = () =>
    view === "addCard" ? setView("default") : onClose();

  const onAddNewCard = (data: { email: string; make_charge: boolean }) => {
    const return_url = `${window.location.origin}`;

    enrollPaymentMethod(
      { ...data, return_url },
      {
        onSuccess: (response) => {
          window.location.href = response.confirmation_url;
        },
        onError: () => {
          showToast("Не удалось добавить карту.", "error");
        },
      },
    );
  };

  const handleAddCard = () => {
    const currentEmail = getValues("email");
    if (!currentEmail || !/^\S+@\S+\.\S+$/.test(currentEmail)) {
      showToast("Пожалуйста, введите корректный E-mail для чеков", "info");
      return;
    }
    const isSbp = paymentMethod === "sbp";
    onAddNewCard({
      email: currentEmail,
      make_charge: isSbp,
    });
  };

  return (
    <>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {view === "addCard" ? "Новая карта" : "Пополнение баланса"}
          </h2>
          <button className={styles.closeButton} onClick={handleCloseModal}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className={styles.content}>
          {view === "default" ? (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className={styles.paymentInputSum}>
                <div
                  className={`${styles.paymentInputGroup} ${
                    errors.amount ? styles.promoError : ""
                  }`}
                >
                  <div className={styles.paymentInputLabel}>
                    Сумма пополнения
                  </div>
                  <input
                    type="number"
                    className={`${styles.paymentInput} ${styles.labeledPaymentInput}`}
                    placeholder="1 000..."
                    {...register("amount")}
                  />
                </div>
                <div className={styles.quickAmounts}>
                  {quickAmounts.map((value) => (
                    <button
                      type="button"
                      key={value}
                      className={styles.quickAmountButton}
                      onClick={() =>
                        setValue("amount", String(value), {
                          shouldValidate: true,
                        })
                      }
                    >
                      {value.toLocaleString("ru-RU")} ₽
                    </button>
                  ))}
                </div>
                {errors.amount && (
                  <p className={styles.errorMessage}>{errors.amount.message}</p>
                )}
              </div>

              {bonusTiers.length > 0 && (
                <div className={styles.progressSection}>
                  {bonusTiers.map((tier) => (
                    <BonusProgress
                      key={tier.id}
                      currentAmount={numericAmount}
                      threshold={tier.min_amount / 100}
                      label={`от ${(tier.min_amount / 100).toLocaleString(
                        "ru-RU",
                      )}`}
                      benefit={`+${tier.bonus_percent}%`}
                    />
                  ))}
                </div>
              )}

              <Dropdown
                label="Способ оплаты"
                options={paymentOptions}
                selectedValue={paymentMethod}
                onSelect={setPaymentMethod}
              />
              {(paymentMethod === "card" || paymentMethod === "sbp") && (
                <CardCarousel
                  onAddCardClick={handleAddCard}
                  savedCards={savedCards}
                  selectedCardId={selectedCardId}
                  onSelectCard={setSelectedCardId}
                  isLoading={isLoadingSavedMethods}
                  paymentMethod={paymentMethod}
                  isAddingCard={isAddingCard}
                />
              )}
              <div className={styles.paymentInputWrap}>
                <div
                  className={`${styles.paymentInputGroup} ${
                    errors.email ? styles.promoError : ""
                  }`}
                >
                  <div className={styles.paymentInputLabel}>
                    Почта для чеков
                  </div>
                  <input
                    placeholder="Почта..."
                    className={`${styles.paymentInput} ${styles.labeledPaymentInput}`}
                    type="email"
                    {...register("email")}
                  />
                </div>
                {errors.email && (
                  <p className={styles.errorMessage}>{errors.email.message}</p>
                )}
              </div>

              <div className={styles.summarySection}>
                {IS_BONUS_SYSTEM_ENABLED && (
                  <>
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>Бонус:</span>
                      <span
                        className={`${styles.summaryValue} ${styles.bonusActive}`}
                      >
                        {bonusAmount > 0
                          ? `+${bonusAmount.toLocaleString("ru-RU")} ₽`
                          : "0 ₽"}
                      </span>
                    </div>
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>
                        Будет зачислено:
                      </span>
                      <span className={styles.summaryValue}>
                        {totalAmount.toLocaleString("ru-RU")} ₽
                      </span>
                    </div>
                  </>
                )}
                <div className={`${styles.summaryRow} ${styles.totalRow}`}>
                  <span className={styles.totalLabel}>К оплате:</span>
                  <span className={styles.totalAmount}>
                    {numericAmount.toLocaleString("ru-RU")} ₽
                  </span>
                </div>
              </div>

              <div className={styles.buttons}>
                <button
                  type="submit"
                  className={styles.confirmButton}
                  disabled={isPending}
                >
                  {isPending ? <div className={styles.spinner} /> : "Пополнить"}
                </button>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={onClose}
                >
                  Отмена
                </button>
              </div>
            </form>
          ) : (
            <AddCardForm
              onCancel={() => setView("default")}
              onSuccess={() => setView("default")}
            />
          )}
        </div>
      </div>
      {view === "default" && (
        <>
          <div
            className={cn(styles.modal, styles.switcherCard)}
            onClick={(e) => {
              e.stopPropagation();
              if (!isAutoTopUpLoading) {
                handleAutoTopUpChange(!autoTopUp);
              }
            }}
          >
            <div className={styles.content}>
              <div className={styles.autoTopUpToggle}>
                <label
                  htmlFor="auto-top-up"
                  onClick={(e) => e.stopPropagation()}
                >
                  <RefreshIcon />
                  <div>
                    <p className={styles.switchTitle}>Автопополнять баланс</p>
                  </div>
                </label>
                <Switch
                  className={styles.switch}
                  id="auto-top-up"
                  checked={autoTopUp}
                  onCheckedChange={handleAutoTopUpChange}
                  disabled={isAutoTopUpLoading}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          </div>
          {autoTopUp && (
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <AutoTopUpForm
                ref={autoTopUpFormRef}
                autoTopUpInfo={autoTopUpInfo}
                selectedCardId={selectedCardId}
                email={emailValue}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};
